import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { gpx } from '@tmcw/togeojson';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import { MAPBOX_ACCESS_TOKEN } from './mapboxConfig.js';

// Your Mapbox access token
mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

// Initial map configuration constants (outside component to avoid dependency issues)
const INITIAL_LNG = -122.486052;
const INITIAL_LAT = 37.830348;
const INITIAL_ZOOM = 12;

function MapWithGpx() {
    const mapContainer = useRef(null);
    const map = useRef(null);
    const navigate = useNavigate();

    const [lng, setLng] = useState(INITIAL_LNG);
    const [lat, setLat] = useState(INITIAL_LAT);
    const [zoom, setZoom] = useState(INITIAL_ZOOM);
    const [trackMetadata, setTrackMetadata] = useState(null);

    // Initialize map when component mounts
    useEffect(() => {
        if (map.current) return; // initialize map only once

        map.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: 'mapbox://styles/mapbox/outdoors-v11',
            center: [INITIAL_LNG, INITIAL_LAT],
            zoom: INITIAL_ZOOM
        });

        // Add navigation controls
        map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

        // Save map state on move
        map.current.on('move', () => {
            setLng(map.current.getCenter().lng.toFixed(4));
            setLat(map.current.getCenter().lat.toFixed(4));
            setZoom(map.current.getZoom().toFixed(2));
        });
    }, []); // Empty dependency array is correct - we only want to initialize once

    // Calculate track statistics from GeoJSON
    const calculateTrackStats = (geoJSON) => {
        let totalDistance = 0;
        let minElevation = Infinity;
        let maxElevation = -Infinity;
        let elevationGain = 0;
        let startTime = null;
        let endTime = null;

        // Find track features
        const trackFeatures = geoJSON.features.filter(f =>
            f.geometry.type === 'LineString' || f.geometry.type === 'MultiLineString'
        );

        trackFeatures.forEach(track => {
            let coords = track.geometry.type === 'LineString'
                ? track.geometry.coordinates
                : track.geometry.coordinates.flat();

            // Calculate distance
            for (let i = 1; i < coords.length; i++) {
                const [lon1, lat1, elev1] = coords[i-1];
                const [lon2, lat2, elev2] = coords[i];

                // Calculate distance between points using Haversine formula
                const R = 6371e3; // Earth's radius in meters
                const φ1 = lat1 * Math.PI/180;
                const φ2 = lat2 * Math.PI/180;
                const Δφ = (lat2-lat1) * Math.PI/180;
                const Δλ = (lon2-lon1) * Math.PI/180;

                const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
                    Math.cos(φ1) * Math.cos(φ2) *
                    Math.sin(Δλ/2) * Math.sin(Δλ/2);
                const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
                const distance = R * c;

                totalDistance += distance;

                // Process elevation data if available
                if (elev1 !== undefined && elev2 !== undefined) {
                    minElevation = Math.min(minElevation, elev1, elev2);
                    maxElevation = Math.max(maxElevation, elev1, elev2);

                    // Calculate elevation gain (only positive changes)
                    const elevDiff = elev2 - elev1;
                    if (elevDiff > 0) {
                        elevationGain += elevDiff;
                    }
                }
            }

            // Extract time data if available
            if (track.properties && track.properties.coordTimes) {
                const times = track.properties.coordTimes.map(t => new Date(t));
                const trackStartTime = times[0];
                const trackEndTime = times[times.length - 1];

                if (!startTime || trackStartTime < startTime) {
                    startTime = trackStartTime;
                }

                if (!endTime || trackEndTime > endTime) {
                    endTime = trackEndTime;
                }
            }
        });

        // Prepare track name and description
        let trackName = "Unknown Track";
        let trackDesc = "";

        if (trackFeatures.length > 0 && trackFeatures[0].properties) {
            trackName = trackFeatures[0].properties.name || trackName;
            trackDesc = trackFeatures[0].properties.desc || "";
        }

        // Duration calculation if time data available
        let duration = null;
        if (startTime && endTime) {
            duration = (endTime - startTime) / 1000; // in seconds
        }

        return {
            name: trackName,
            description: trackDesc,
            distance: totalDistance, // in meters
            minElevation: minElevation !== Infinity ? minElevation : null,
            maxElevation: maxElevation !== -Infinity ? maxElevation : null,
            elevationGain,
            startTime,
            endTime,
            duration
        };
    };

    // Extract extended data from original GPX DOM
    const extractExtendedData = (gpxDOM) => {
        // Get track points with extension data
        const trackPoints = Array.from(gpxDOM.querySelectorAll('trkpt'));

        // Check for common extensions
        const hasHeartRate = trackPoints.some(pt => pt.querySelector('extensions > *|hr, extensions > gpxtpx\\:TrackPointExtension > gpxtpx\\:hr'));
        const hasCadence = trackPoints.some(pt => pt.querySelector('extensions > *|cad, extensions > gpxtpx\\:TrackPointExtension > gpxtpx\\:cad'));
        const hasTemperature = trackPoints.some(pt => pt.querySelector('extensions > *|temp, extensions > gpxtpx\\:TrackPointExtension > gpxtpx\\:temp'));

        // Get any custom extension namespaces
        const extensionNamespaces = new Set();
        trackPoints.forEach(pt => {
            const extensions = pt.querySelector('extensions');
            if (extensions) {
                Array.from(extensions.children).forEach(ext => {
                    extensionNamespaces.add(ext.namespaceURI);
                });
            }
        });

        return {
            hasHeartRate,
            hasCadence,
            hasTemperature,
            extensionNamespaces: Array.from(extensionNamespaces)
        };
    };

    // Function to parse GPX and add to map
    const addGpxToMap = (gpxText) => {
        if (!map.current || !map.current.loaded()) return;

        try {
            // Parse GPX to DOM
            const parser = new DOMParser();
            const gpxDOM = parser.parseFromString(gpxText, 'text/xml');

            // Extract extended data information
            const extendedData = extractExtendedData(gpxDOM);

            // Convert to GeoJSON
            const geoJSON = gpx(gpxDOM);

            // Debug logging
            console.log('GeoJSON structure:', JSON.stringify(geoJSON, null, 2));
            console.log('Track features:', geoJSON.features.filter(f =>
                f.geometry.type === 'LineString' || f.geometry.type === 'MultiLineString'
            ).length);

            // Calculate track statistics
            const stats = calculateTrackStats(geoJSON);
            setTrackMetadata({ ...stats, extensions: extendedData });

            // Remove existing layers and source properly
            if (map.current.getSource('gpx-track')) {
                // Get all layers that use this source
                const layers = map.current.getStyle().layers;
                const layersToRemove = layers
                    .filter(layer => layer.source === 'gpx-track')
                    .map(layer => layer.id);

                // Remove each layer
                layersToRemove.forEach(layerId => {
                    if (map.current.getLayer(layerId)) {
                        map.current.removeLayer(layerId);
                    }
                });

                // Remove the source
                map.current.removeSource('gpx-track');
            }

            // Add the GeoJSON as a source
            map.current.addSource('gpx-track', {
                type: 'geojson',
                data: geoJSON
            });

            // Check if we have track features
            const trackFeatures = geoJSON.features.filter(f =>
                f.geometry.type === 'LineString' || f.geometry.type === 'MultiLineString'
            );

            if (trackFeatures.length === 0) {
                console.warn('No track data found in GPX file');
                // You could show an alert to the user here
            } else {
                // Add a line layer for the track with simplified filter
                map.current.addLayer({
                    id: 'gpx-track-line',
                    type: 'line',
                    source: 'gpx-track',
                    layout: {
                        'line-join': 'round',
                        'line-cap': 'round',
                        'visibility': 'visible'
                    },
                    paint: {
                        'line-color': '#FF0000',
                        'line-width': 4
                    }
                });

                // Add color-coded elevation if available
                const hasElevation = trackFeatures.some(f =>
                    f.geometry.coordinates.some(coord => coord.length > 2)
                );

                if (hasElevation && stats.minElevation !== null && stats.maxElevation !== null) {
                    // Create a color-coded track by elevation
                    map.current.addLayer({
                        id: 'gpx-track-elevation',
                        type: 'line',
                        source: 'gpx-track',
                        filter: ['any',
                            ['==', ['geometry-type'], 'LineString'],
                            ['==', ['geometry-type'], 'MultiLineString']
                        ],
                        layout: {
                            'line-join': 'round',
                            'line-cap': 'round',
                            'visibility': 'visible'
                        },
                        paint: {
                            'line-color': [
                                'interpolate',
                                ['linear'],
                                ['get', 'ele'],
                                stats.minElevation || 0, '#0000FF', // Blue for lowest elevation
                                (stats.minElevation + stats.maxElevation) / 2 || 500, '#00FF00', // Green for middle elevation
                                stats.maxElevation || 1000, '#FF0000'  // Red for highest elevation
                            ],
                            'line-width': 5,
                            'line-opacity': 0.7
                        }
                    });
                }
            }

            // Add points for waypoints with popups
            const waypoints = geoJSON.features.filter(f => f.geometry.type === 'Point');
            if (waypoints.length > 0) {
                map.current.addLayer({
                    id: 'gpx-waypoints',
                    type: 'circle',
                    source: 'gpx-track',
                    filter: ['==', '$type', 'Point'],
                    paint: {
                        'circle-radius': 6,
                        'circle-color': '#0088FF',
                        'circle-stroke-width': 2,
                        'circle-stroke-color': '#FFFFFF'
                    }
                });

                // Add a symbol layer for waypoint names
                map.current.addLayer({
                    id: 'gpx-waypoint-labels',
                    type: 'symbol',
                    source: 'gpx-track',
                    filter: ['==', '$type', 'Point'],
                    layout: {
                        'text-field': ['get', 'name'],
                        'text-font': ['Open Sans Regular'],
                        'text-size': 12,
                        'text-offset': [0, 1.5],
                        'text-anchor': 'top'
                    },
                    paint: {
                        'text-color': '#000000',
                        'text-halo-color': '#FFFFFF',
                        'text-halo-width': 1
                    }
                });

                // Add popups for waypoints
                waypoints.forEach(waypoint => {
                    const coordinates = waypoint.geometry.coordinates.slice();
                    const name = waypoint.properties.name || 'Waypoint';
                    const description = waypoint.properties.desc || '';
                    const elevation = waypoint.properties.ele ? `Elevation: ${waypoint.properties.ele.toFixed(1)}m` : '';
                    const time = waypoint.properties.time ? `Time: ${new Date(waypoint.properties.time).toLocaleString()}` : '';

                    // Create popup content
                    const popupContent = document.createElement('div');
                    popupContent.innerHTML = `
                        <h3>${name}</h3>
                        ${description ? `<p>${description}</p>` : ''}
                        ${elevation ? `<p>${elevation}</p>` : ''}
                        ${time ? `<p>${time}</p>` : ''}
                    `;

                    // Create and add the popup
                    const popup = new mapboxgl.Popup({ offset: 25 })
                        .setDOMContent(popupContent);

                    new mapboxgl.Marker({ color: '#0088FF' })
                        .setLngLat(coordinates)
                        .setPopup(popup)
                        .addTo(map.current);
                });
            }

            // Fit the map to show the entire track
            const bounds = new mapboxgl.LngLatBounds();
            geoJSON.features.forEach(feature => {
                if (feature.geometry.type === 'LineString') {
                    feature.geometry.coordinates.forEach(coord => {
                        bounds.extend(coord);
                    });
                } else if (feature.geometry.type === 'MultiLineString') {
                    feature.geometry.coordinates.forEach(line => {
                        line.forEach(coord => {
                            bounds.extend(coord);
                        });
                    });
                } else if (feature.geometry.type === 'Point') {
                    bounds.extend(feature.geometry.coordinates);
                }
            });

            if (!bounds.isEmpty()) {
                map.current.fitBounds(bounds, { padding: 50 });
            }

            // Add start and end markers for the track
            if (trackFeatures.length > 0) {
                let startCoord, endCoord;

                if (trackFeatures[0].geometry.type === 'LineString') {
                    startCoord = trackFeatures[0].geometry.coordinates[0];
                    endCoord = trackFeatures[0].geometry.coordinates[trackFeatures[0].geometry.coordinates.length - 1];
                } else { // MultiLineString
                    const allLines = trackFeatures[0].geometry.coordinates;
                    startCoord = allLines[0][0];
                    endCoord = allLines[allLines.length - 1][allLines[allLines.length - 1].length - 1];
                }

                // Add start marker
                new mapboxgl.Marker({ color: '#00FF00' })
                    .setLngLat(startCoord)
                    .setPopup(new mapboxgl.Popup().setHTML('<h3>Start</h3>'))
                    .addTo(map.current);

                // Add end marker
                new mapboxgl.Marker({ color: '#FF0000' })
                    .setLngLat(endCoord)
                    .setPopup(new mapboxgl.Popup().setHTML('<h3>End</h3>'))
                    .addTo(map.current);
            }

        } catch (error) {
            console.error('Error processing GPX file:', error);
            alert('Error processing GPX file. Please check the format and try again.');
        }
    };

    // Handle file upload
    const { getRootProps, getInputProps } = useDropzone({
        accept: {
            'application/gpx+xml': ['.gpx']
        },
        onDrop: acceptedFiles => {
            const file = acceptedFiles[0];
            if (file) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    const gpxText = e.target.result;
                    addGpxToMap(gpxText);
                };
                reader.readAsText(file);
            }
        }
    });

    // URL input for loading GPX from a URL
    const [gpxUrl, setGpxUrl] = useState('');

    const loadFromUrl = async (e) => {
        e.preventDefault();
        if (!gpxUrl) return;

        try {
            const response = await fetch(gpxUrl);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const gpxText = await response.text();
            addGpxToMap(gpxText);
        } catch (error) {
            console.error('Error fetching GPX file:', error);
            alert('Error fetching GPX file. Please check the URL and try again.');
        }
    };

    // Format distance nicely
    const formatDistance = (meters) => {
        if (meters < 1000) {
            return `${meters.toFixed(0)} m`;
        } else {
            return `${(meters / 1000).toFixed(2)} km`;
        }
    };

    // Format duration nicely
    const formatDuration = (seconds) => {
        if (!seconds) return 'N/A';

        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);

        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div className="map-container">
            <div className="sidebar">
                <div className="coordinates">
                    Longitude: {lng} | Latitude: {lat} | Zoom: {zoom}
                </div>

                {trackMetadata && (
                    <div className="track-info">
                        <h3>{trackMetadata.name}</h3>
                        {trackMetadata.description && <p>{trackMetadata.description}</p>}

                        <table>
                            <tbody>
                            <tr>
                                <td>Distance:</td>
                                <td>{formatDistance(trackMetadata.distance)}</td>
                            </tr>
                            {trackMetadata.duration && (
                                <tr>
                                    <td>Duration:</td>
                                    <td>{formatDuration(trackMetadata.duration)}</td>
                                </tr>
                            )}
                            {trackMetadata.minElevation !== null && (
                                <tr>
                                    <td>Elevation Range:</td>
                                    <td>{trackMetadata.minElevation.toFixed(0)}m - {trackMetadata.maxElevation.toFixed(0)}m</td>
                                </tr>
                            )}
                            {trackMetadata.elevationGain > 0 && (
                                <tr>
                                    <td>Elevation Gain:</td>
                                    <td>{trackMetadata.elevationGain.toFixed(0)}m</td>
                                </tr>
                            )}
                            {trackMetadata.startTime && (
                                <tr>
                                    <td>Start Time:</td>
                                    <td>{trackMetadata.startTime.toLocaleString()}</td>
                                </tr>
                            )}
                            </tbody>
                        </table>

                        <div className="track-extensions">
                            <h4>Available Data:</h4>
                            <ul>
                                {trackMetadata.extensions.hasHeartRate && <li>Heart Rate</li>}
                                {trackMetadata.extensions.hasCadence && <li>Cadence</li>}
                                {trackMetadata.extensions.hasTemperature && <li>Temperature</li>}
                            </ul>
                        </div>
                    </div>
                )}

                <div className="upload-container">
                    <h3>Load GPX File</h3>

                    <div {...getRootProps({ className: 'dropzone' })}>
                        <input {...getInputProps()} />
                        <p>Drag & drop a GPX file here, or click to select one</p>
                    </div>

                    <div className="url-input">
                        <h4>Or load from URL:</h4>
                        <form onSubmit={loadFromUrl}>
                            <input
                                type="text"
                                value={gpxUrl}
                                onChange={(e) => setGpxUrl(e.target.value)}
                                placeholder="https://example.com/track.gpx"
                            />
                            <button type="submit">Load</button>
                        </form>

                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="back-button"
                        >
                            Back
                        </button>
                    </div>
                </div>
            </div>

            <div ref={mapContainer} className="map" />

            <style jsx>{`
        .map-container {
          position: relative;
          height: 100vh;
        }
        .map {
          position: absolute;
          top: 0;
          bottom: 0;
          width: 100%;
        }
        .sidebar {
          background-color: rgba(35, 55, 75, 0.9);
          color: #fff;
          padding: 6px 12px;
          font-family: monospace;
          z-index: 1;
          position: absolute;
          top: 0;
          left: 0;
          margin: 12px;
          border-radius: 4px;
          max-width: 300px;
          max-height: 90vh;
          overflow-y: auto;
        }
        .track-info {
          margin: 10px 0;
          border-top: 1px solid #ccc;
          padding-top: 10px;
        }
        .track-info table {
          width: 100%;
          border-collapse: collapse;
        }
        .track-info td {
          padding: 3px 0;
        }
        .track-info td:first-child {
          font-weight: bold;
        }
        .track-extensions {
          margin-top: 10px;
        }
        .track-extensions ul {
          margin: 5px 0;
          padding-left: 20px;
        }
        .dropzone {
          border: 2px dashed #ccc;
          border-radius: 4px;
          padding: 20px;
          text-align: center;
          margin: 10px 0;
          cursor: pointer;
        }
        .url-input {
          margin-top: 10px;
        }
        .url-input input {
          width: 100%;
          padding: 5px;
          margin-bottom: 5px;
        }
        .url-input button {
          width: 100%;
          padding: 5px;
          margin-bottom: 5px;
        }
        .back-button {
          background-color: #007bff;
          color: white;
          border: none;
          border-radius: 4px;
        }
        .back-button:hover {
          background-color: #0056b3;
        }
      `}</style>
        </div>
    );
}

export default MapWithGpx;